import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { IFormElementOptionProp, initialLegalEntityFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { FormSelect } from 'components/molecules/FormSelect';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { Box, Typography } from '@mui/material';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { EDictionaryPrefixTypesUniqueId, mapDictionaryToOptionProp } from 'helpers/dictionary';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { useTranslations } from 'hooks/useTranslations';
import { useIndLEFormSchema } from 'pages/LegalEntitiesPage/schemaValidation';
import { EDictionaryPrefixTypes, EDictionaryTypes, IDictionary } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ECustomFieldObjectType, ECustomFieldType } from 'models/customField';
import FormCustomField from 'components/organisms/FormCustomField';
import { ICustomFieldForm } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { useAuth } from 'models/authContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { formatDateTimeToIso } from 'utils/date';
import { useScroll } from 'hooks/useScroll';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { ELegalVisibility } from 'constants/LegalVisibility';
import { useAccount } from 'pages/AccountPage/hooks';
import { mapBlankToNull } from 'utils/form';
import { FormModal } from 'components/molecules/Modal';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { sortBy } from 'helpers/licences';
import { SubsectionHeader } from './styles';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { LEModuleTypes } from 'constants/LegalEntityTypes';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';

export interface ILegalEntityFormValues {
  uniqueId: string;
  firstName: string;
  lastName: string;
  secondSurname: string;
  dateOfBirth: string | null;
  genderUniqueId: any;
  visibilityUniqueId: string;
  customFields?: ICustomFieldForm[];
  identityDocumentNumber: string | null;
  identityDocumentTypeUniqueId: string | null;
}

export interface ILegalEntityForm {
  onCancel?: () => void;
  onSubmit: (arg: ILegalEntityFormValues) => void;
  initialFormValues?: ILegalEntityData | null;
  isEdit?: boolean;
  isDisabled?: boolean;
}

const LegalEntityForm = ({
  onCancel,
  onSubmit,
  initialFormValues,
  isEdit = false,
  isDisabled = false,
}: ILegalEntityForm) => {
  const { selectedLanguage } = useLang();
  const { hasPermissions } = usePermission();
  const { account, getSelfAccountData } = useAccount();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const {
    getFieldsOptional,
    fieldsOptional,
    prepareFormByFieldsOptional,
    hiddenFieldsName,
    requiredFieldsName,
  } = useSystemFieldsConfiguration();

  useEffect(() => {
    getFieldsOptional([LEModuleTypes.individual]);
  }, []);

  useEffect(() => {
    if (fieldsOptional.length) {
      prepareFormByFieldsOptional(fieldsOptional, setValue);
    }
  }, [fieldsOptional]);

  const {
    loadDictionaries,
    gender,
    weaponLicencingAuthority,
    legalVisibility,
    legalEntityPrefix,
    civilianPrefix,
    militaryPrefix,
    policePrefix,
    getCustomField,
    customFieldsILE,
    nationality,
    identificationDocumentType,
  } = useLegalEntities();
  const { elRef, scrollToError } = useScroll();
  const [open, setOpen] = React.useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const [prefixOptions, setPrefixOptions] = useState<IDictionary[] | null>(null);
  const [prefixOptionLabel, setPrefixOptionLabel] = useState<EDictionaryPrefixTypes | null>(
    null,
  );
  const [noLA, setNoLA] = useState(false);
  const [legalEntityPrefixFilter, setLegalEntityPrefixFilter] = useState<IDictionary[]>([]);
  const { t } = useTranslations();
  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();
  const { getUser, user } = useAuth();
  const { shortDateFormat, unit, getProperties } = useGlobalProperty();
  const { schema } = useIndLEFormSchema(
    customFieldsILE,
    requiredFieldsName,
    hiddenFieldsName,
    !!hasWeaponLicenceModule(),
    initialFormValues?.customFields,
  );
  const { setAlert } = useAlert();
  const [optionsVisibility, setOptionsVisibility] = useState<
    IFormElementOptionProp[] | undefined
  >(undefined);
  const [isDisableVisibility, setIsDisableVisibility] = useState<boolean>(true);
  let wasCustomFieldsDataChanged = false;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    getValues,
  } = useForm<ILegalEntityData | ILegalEntityFormValues>({
    defaultValues: initialFormValues || initialLegalEntityFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<
      ILegalEntityData | ILegalEntityFormValues
    >,
  });

  useEffect(() => {
    getProperties();
    getSelfAccountData();
  }, []);

  useEffect(() => {
    reset(initialFormValues || initialLegalEntityFormValues);
  }, [reset, initialFormValues]);

  useEffect(() => {
    if (user === null) {
      getUser();
    }
  }, []);

  useEffect(() => {
    if (legalVisibility) {
      setOptionsVisibility(
        mapDictionaryToOptionProp(legalVisibility.concat().sort(sortBy('name'))),
      );
    }
  }, [legalVisibility]);

  useEffect(() => {
    if (
      account &&
      account.licenceAuthorityIds.length === 1 &&
      !isEdit &&
      hasWeaponLicenceModule()
    ) {
      setValue('primaryWeaponLicencingAuthorityUniqueId', account.licenceAuthorityIds[0]);
    }
  }, [account]);

  useEffect(() => {
    if (optionsVisibility && !hasPermissions([EPermission.VIEW_ALL_LE])) {
      if (!isEdit) {
        setValue('visibilityUniqueId', ELegalVisibility.PUBLIC as string);
      }
      setIsDisableVisibility(true);
    } else {
      setIsDisableVisibility(false);
    }
  }, [initialFormValues, isEdit, optionsVisibility]);

  useEffect(() => {
    if (user) {
      (async function getUserDataInit() {
        if (user) {
          if (user.prefixGroupIds && user.prefixGroupIds.length) {
            if (legalEntityPrefix) {
              const names = legalEntityPrefix.filter((prefix) =>
                user.prefixGroupIds.includes(prefix.uniqueId.toString()),
              );
              setLegalEntityPrefixFilter(names);
            }
          }
        }
      })();
    }
  }, [user, legalEntityPrefix]);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  const hasDataChanged = (data: any): boolean => {
    let hasChanged = false;
    if (
      initialFormValues?.dateOfBirth !== data.dateOfBirth ||
      initialFormValues?.placeOfBirth !== data.placeOfBirth ||
      initialFormValues?.firstName !== data.firstName ||
      initialFormValues?.middleNames !== data.middleNames ||
      initialFormValues?.lastName !== data.lastName ||
      initialFormValues?.secondSurname !== data.secondSurname ||
      initialFormValues?.genderUniqueId !== data.genderUniqueId ||
      initialFormValues?.legalType !== data.legalType ||
      initialFormValues?.visibilityUniqueId !== data.visibilityUniqueId ||
      initialFormValues?.prefixGroupUniqueId !== data.prefixGroupUniqueId ||
      initialFormValues?.prefixValueUniqueId !== data.prefixValueUniqueId ||
      initialFormValues?.nationalityUniqueId !== data.nationalityUniqueId ||
      initialFormValues?.height !== data.height ||
      mapBlankToNull(initialFormValues?.occupation?.trim()) !==
        mapBlankToNull(data.occupation?.trim()) ||
      mapBlankToNull(initialFormValues?.identityDocumentNumber?.trim()) !==
        mapBlankToNull(data.identityDocumentNumber?.trim()) ||
      mapBlankToNull(initialFormValues?.identityDocumentTypeUniqueId?.trim()) !==
        mapBlankToNull(data.identityDocumentTypeUniqueId?.trim()) ||
      initialFormValues?.primaryWeaponLicencingAuthorityUniqueId !==
        data.primaryWeaponLicencingAuthorityUniqueId ||
      initialFormValues?.secondaryWeaponLicencingAuthorityUniqueId !==
        data.secondaryWeaponLicencingAuthorityUniqueId
    ) {
      hasChanged = true;
    }

    customFieldsILE.forEach((customField) => {
      let matchingDataCustomFieldValue;
      // eslint-disable-next-line default-case
      switch (customField.customFieldType) {
        case ECustomFieldType.DATE: {
          const value = data[customField.name];
          if (value !== undefined && value !== null) {
            matchingDataCustomFieldValue = formatDateTimeToIso(value);
          }
          break;
        }
        case ECustomFieldType.BOOL: {
          const value = data[customField.name];
          if (value !== undefined && value !== null && value.trim() !== '') {
            matchingDataCustomFieldValue = data[customField.name] === '1';
          }
          break;
        }
        case ECustomFieldType.TEXT: {
          const value = data[customField.name];
          if (value !== undefined && value !== null && value.trim() !== '') {
            matchingDataCustomFieldValue = data[customField.name];
          }
          break;
        }
      }
      let initialCustomField;
      let initCustomFieldData;
      if (!!initialFormValues && initialFormValues.customFields !== undefined) {
        initialCustomField = initialFormValues.customFields.find(
          (initCustomField) => initCustomField.customFieldUniqueId === customField.uniqueId,
        );
        initCustomFieldData =
          initialCustomField &&
          initialCustomField.value !== undefined &&
          initialCustomField.value !== null
            ? initialCustomField.value
            : undefined;
      }
      if (initCustomFieldData !== matchingDataCustomFieldValue) {
        hasChanged = true;
        wasCustomFieldsDataChanged = true;
      }
    });

    return hasChanged;
  };

  const onSubmitForm = async (data: any) => {
    if (wasCustomFieldsDataChanged) {
      data.customFields = [];

      const customFieldPayload: ICustomFieldForm[] = [];

      Object.keys(data).map((key) => {
        const customField = customFieldsILE.find((item) => item.name === key);
        if (customField) {
          const customFieldItem: ICustomFieldForm = {
            customFieldUniqueId: customField.uniqueId,
          };
          if (customField.customFieldType === ECustomFieldType.TEXT) {
            if (data[key] === undefined || data[key] === null) {
              customFieldItem.text = undefined;
            } else if (data[key].trim() === '') {
              customFieldItem.text = undefined;
            } else {
              customFieldItem.text = data[key];
            }
          } else if (customField.customFieldType === ECustomFieldType.BOOL) {
            if (data[key] === '') {
              customFieldItem.bool = null;
            } else {
              customFieldItem.bool = data[key] === '1';
            }
          } else if (customField.customFieldType === ECustomFieldType.DATE) {
            customFieldItem.date = data[key] ? formatDateTimeToIso(data[key]) : null;
          }
          customFieldPayload.push(customFieldItem);
          delete data[key];
        }
        return false;
      });

      if (customFieldPayload?.length && customFieldPayload.length > 0) {
        data.customFields = customFieldPayload;
      } else {
        delete data.customFields;
      }
    } else {
      delete data.customFields;
    }

    if (prefixOptions !== civilianPrefix) {
      delete data.primaryWeaponLicencingAuthorityUniqueId;
      delete data.secondaryWeaponLicencingAuthorityUniqueId;
      delete data.identityDocumentNumber;
      delete data.identityDocumentTypeUniqueId;
    }

    setIsSubmitLoading(true);
    if (initialFormValues) {
      await onSubmit({
        ...data,
        dateOfBirth:
          data.dateOfBirth !== null
            ? formatDateTimeToIso(data.dateOfBirth)
            : formatDateTimeToIso(new Date().toString()),
      });
      setIsSubmitLoading(false);
    } else {
      await onSubmit(data);
      setIsSubmitLoading(false);
    }
    setIsSubmitLoading(false);
  };

  const onSubmitHandle = async (data: any) => {
    if (!data?.middleNames?.trim().length) {
      data.middleNames = null;
    }

    if (!hasDataChanged(data)) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
      return;
    }
    if (
      hasWeaponLicenceModule() &&
      prefixOptions === civilianPrefix &&
      account?.licenceAuthorityIds.length &&
      !isAnyLicenceAuthorityValid([
        data.primaryWeaponLicencingAuthorityUniqueId,
        data.secondaryWeaponLicencingAuthorityUniqueId,
      ])
    ) {
      setPendingSubmit(data);
      setOpen(true);
    } else {
      await onSubmitForm(data);
    }
  };

  const checkIsNoLA = (isCivilian = false) => {
    if (
      isCivilian &&
      account &&
      !account.licenceAuthorityIds.length &&
      hasWeaponLicenceModule()
    ) {
      setNoLA(true);
    } else {
      setNoLA(false);
    }
  };

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.Gender,
        EDictionaryTypes.LegalVisibility,
        EDictionaryTypes.legalEntityPrefix,
        EDictionaryTypes.civilianPrefix,
        EDictionaryTypes.militaryPrefix,
        EDictionaryTypes.policePrefix,
        EDictionaryTypes.weaponLicencingAuthority,
        EDictionaryTypes.nationality,
      ]);
      await getCustomField(ECustomFieldObjectType.LE_INDIVIDUAL);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    const value = getValues('prefixGroupUniqueId');
    if (legalEntityPrefixFilter?.length && !value) {
      setValue('prefixGroupUniqueId', legalEntityPrefixFilter[0].uniqueId.toString());
      setPrefixOptions(civilianPrefix);
      checkIsNoLA(true);
      setPrefixOptionLabel(EDictionaryPrefixTypes.civilianPrefix);
    } else if (legalEntityPrefixFilter?.length && value) {
      checkIsNoLA(value === EDictionaryPrefixTypesUniqueId.civilianPrefix);
      switch (value) {
        case EDictionaryPrefixTypesUniqueId.civilianPrefix:
          setPrefixOptions(civilianPrefix);
          setPrefixOptionLabel(EDictionaryPrefixTypes.civilianPrefix);
          break;
        case EDictionaryPrefixTypesUniqueId.militaryPrefix:
          setPrefixOptions(militaryPrefix);
          setPrefixOptionLabel(EDictionaryPrefixTypes.militaryPrefix);
          break;
        case EDictionaryPrefixTypesUniqueId.policePrefix:
          setPrefixOptions(policePrefix);
          setPrefixOptionLabel(EDictionaryPrefixTypes.policePrefix);
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntityPrefixFilter, civilianPrefix, militaryPrefix, policePrefix]);

  const legalEntityPrefixChanged = (target: any) => {
    const value = target.value ?? '';
    checkIsNoLA(value === EDictionaryPrefixTypesUniqueId.civilianPrefix);
    switch (value) {
      case EDictionaryPrefixTypesUniqueId.civilianPrefix:
        setValue('prefixValueUniqueId', '');
        setPrefixOptions(civilianPrefix);
        setPrefixOptionLabel(EDictionaryPrefixTypes.civilianPrefix);
        break;
      case EDictionaryPrefixTypesUniqueId.militaryPrefix:
        setValue('prefixValueUniqueId', '');
        setValue('height', null);
        setValue('nationalityUniqueId', null);
        setValue('placeOfBirth', null);
        setValue('occupation', null);
        setPrefixOptions(militaryPrefix);
        setPrefixOptionLabel(EDictionaryPrefixTypes.militaryPrefix);
        break;
      case EDictionaryPrefixTypesUniqueId.policePrefix:
        setValue('prefixValueUniqueId', '');
        setValue('height', null);
        setValue('nationalityUniqueId', null);
        setValue('placeOfBirth', null);
        setValue('occupation', null);
        setPrefixOptions(policePrefix);
        setPrefixOptionLabel(EDictionaryPrefixTypes.policePrefix);
        break;
      default:
        break;
    }
  };

  return (
    <>
      {noLA && (
        <Box sx={{ mb: 2 }}>
          <Alert
            text={t('legalEntity.add.userMissingLicenceAuthority.message')}
            variant={EAlertVariants.warning}
          />
        </Box>
      )}
      <FormModal
        onClickClose={() => setOpen(false)}
        onClickApprove={() => onSubmitForm(pendingSubmit)}
        open={open}
        title={t('general.warning.text')}
        text={t('editLegalEntity.warning.text')}
      />
      <form onSubmit={handleSubmit(onSubmitHandle)}>
        <Grid container columnSpacing={3} ref={elRef}>
          <Grid size={{ xs: 12 }}>
            <SubsectionHeader variant="h5">
              {t('editLegalEntity.subsectionTitle.generalDetails')}
            </SubsectionHeader>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormRadioGroup
              name="prefixGroupUniqueId"
              label={`${t('editLegalEntity.legalEntityPrefix.label')}*`}
              options={mapDictionaryToOptionProp(legalEntityPrefixFilter)}
              control={control}
              errors={errors}
              onChangeOption={legalEntityPrefixChanged}
              disabled={isEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 8 }}>
            <FormRadioGroup
              name="visibilityUniqueId"
              label={`${t('editLegalEntity.visibility.label')}*`}
              options={optionsVisibility ?? []}
              control={control}
              errors={errors}
              disabled={isDisableVisibility || isDisabled}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormSearchSelect
              options={mapDictionaryToOptionProp(prefixOptions)}
              name="prefixValueUniqueId"
              label={`${t(`editLegalEntity.${prefixOptionLabel}.label`)}*`}
              control={control}
              errors={errors}
              disabled={isDisabled}
              canBeEmpty={false}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormInput
              name="firstName"
              label={`${t('editLegalEntity.firstName.label')}*`}
              setValue={setValue}
              control={control}
              errors={errors}
              charsLimit={100}
              disabled={isDisabled}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormInput
              name="middleNames"
              label={t('editLegalEntity.middleNames.label')}
              setValue={setValue}
              control={control}
              errors={errors}
              charsLimit={100}
              disabled={isDisabled}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormInput
              name="lastName"
              label={`${t('editLegalEntity.lastName.label')}*`}
              setValue={setValue}
              control={control}
              errors={errors}
              charsLimit={100}
              disabled={isDisabled}
            />
          </Grid>
          {!hiddenFieldsName.includes(EFieldsOptional.secondSurname) && (
            <Grid size={{ xs: 12, xl: 8 }}>
              <FormInput
                name="secondSurname"
                label={t('legalEntityDetails.secondSurname.label')}
                setValue={setValue}
                control={control}
                errors={errors}
                charsLimit={100}
                disabled={isDisabled}
                required={requiredFieldsName.includes(EFieldsOptional.secondSurname)}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormDatePicker
              name="dateOfBirth"
              dateFormat={shortDateFormat}
              label={`${t('editLegalEntity.dateOfBirth.label')}*`}
              control={control}
              errors={errors}
              disabled={isDisabled}
            />
          </Grid>
          {prefixOptions === civilianPrefix && (
            <>
              {!hiddenFieldsName.includes(EFieldsOptional.placeOfBirth) && (
                <Grid size={{ xs: 12, lg: 4 }}>
                  <FormInput
                    name="placeOfBirth"
                    label={t('editLegalEntity.placeOfBirth.label')}
                    required={requiredFieldsName.includes(EFieldsOptional.placeOfBirth)}
                    setValue={setValue}
                    control={control}
                    errors={errors}
                    charsLimit={64}
                    disabled={isDisabled}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, lg: 4 }}>
                <FormSearchSelect
                  options={mapDictionaryToOptionProp(nationality)}
                  name="nationalityUniqueId"
                  label={t('editLegalEntity.nationality.label')}
                  required={!!hasWeaponLicenceModule()}
                  control={control}
                  errors={errors}
                  disabled={isDisabled}
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(gender)}
              setValue={setValue}
              name="genderUniqueId"
              label={`${t('editLegalEntity.gender.label')}*`}
              control={control}
              errors={errors}
              disabled={isDisabled}
              canBeEmpty={false}
            />
          </Grid>
          {prefixOptions === civilianPrefix && (
            <>
              {!hiddenFieldsName.includes(EFieldsOptional.height) && (
                <Grid size={{ xs: 12, lg: 4 }}>
                  <FormInput
                    name="height"
                    label={t('editLegalEntity.height.label')}
                    required={requiredFieldsName.includes(EFieldsOptional.height)}
                    setValue={setValue}
                    control={control}
                    errors={errors}
                    disabled={isDisabled}
                    charsLimit={10}
                    endAdornmentText={unit}
                  />
                </Grid>
              )}
              {!hiddenFieldsName.includes(EFieldsOptional.occupation) && (
                <Grid size={{ xs: 12, lg: 4 }}>
                  <FormInput
                    name="occupation"
                    label={t('editLegalEntity.occupation.label')}
                    required={requiredFieldsName.includes(EFieldsOptional.occupation)}
                    setValue={setValue}
                    control={control}
                    errors={errors}
                    disabled={isDisabled}
                    charsLimit={64}
                  />
                </Grid>
              )}
            </>
          )}
          {customFieldsILE.map((customField) => (
            <Grid size={{ xs: 12, lg: 4 }} key={customField.uniqueId}>
              <FormCustomField
                data={customField}
                control={control}
                errors={errors}
                setValue={setValue}
                disabled={isDisabled}
              />
            </Grid>
          ))}
          {prefixOptions === civilianPrefix && (
            <>
              <Grid size={{ xs: 12 }}>
                <SubsectionHeader variant="h5">
                  {t('editLegalEntity.subsectionTitle.identification')}
                </SubsectionHeader>
              </Grid>
              <Grid size={{ xs: 12, lg: 4 }}>
                <FormSearchSelect
                  options={mapDictionaryToOptionProp(identificationDocumentType)}
                  name="identityDocumentTypeUniqueId"
                  label={`${t('editLegalEntity.identityDocumentType.label')}`}
                  control={control}
                  errors={errors}
                  canBeEmpty
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 4 }}>
                <FormInput
                  name="identityDocumentNumber"
                  label={`${t('editLegalEntity.identityDocumentNumber.label')}`}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  charsLimit={20}
                  disabled={isDisabled}
                />
              </Grid>

              {hasWeaponLicenceModule() && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <SubsectionHeader variant="h5">
                      {t('editLegalEntity.subsectionTitle.licencingAuthorities')}
                    </SubsectionHeader>
                  </Grid>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <FormSearchSelect
                      options={mapDictionaryToOptionProp(weaponLicencingAuthority)}
                      name="primaryWeaponLicencingAuthorityUniqueId"
                      label={`${t(
                        'editLegalEntity.primaryWeaponLicencingAuthorityUniqueId.label',
                      )}*`}
                      control={control}
                      errors={errors}
                      disabled={
                        isDisabled || (!isEdit && account?.licenceAuthorityIds.length === 1)
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <FormSearchSelect
                      options={mapDictionaryToOptionProp(weaponLicencingAuthority)}
                      name="secondaryWeaponLicencingAuthorityUniqueId"
                      label={t(
                        'editLegalEntity.secondaryWeaponLicencingAuthorityUniqueId.label',
                      )}
                      control={control}
                      errors={errors}
                      disabled={isDisabled}
                    />
                  </Grid>
                </>
              )}
            </>
          )}

          <Grid container spacing={3} size={{ xs: 12 }} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Button
                type="submit"
                fullWidth
                label={t('general.save.button')}
                id="legal_entity-save"
                variant={EButtonVariants.contained}
                isLoading={isSubmitLoading}
                disabled={isDisabled || noLA}
              />
            </Grid>

            {onCancel && (
              <Grid size={{ xs: 12, lg: 4 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  id="legal_entity-cancel"
                  variant={EButtonVariants.outlined}
                  onClick={onCancel}
                />
              </Grid>
            )}
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export { LegalEntityForm };
